import { useQueryClient } from "@tanstack/react-query";
import useAPI, {
  useAPIMutation,
  useDistinctFilterOptions
} from "@/services/api/hook";
import {
  APIObject,
  ComputedFields,
  Filters,
  SearchQuery
} from "@/services/api/types";
import { PartnerContactData } from "./types";
import useBoundStore, {
  MAX_ENTITIES_PER_PAGE
} from "@/services/store/BoundStore";
import { refreshEntityDetail, useEntities } from "@/services/api/entityhooks";
import { EntitySliceState } from "@/services/api/entitystore";
import { createUseFilteredEntitiesInfinite } from "@/services/api/infinite";

export const MAX_PARTNER_CONTACTS_PER_PAGE = MAX_ENTITIES_PER_PAGE;

export const usePartnerContacts = (
  ids: string[],
  options: {
    enabled?: boolean;
    computed_fields?: ComputedFields;
    forceRefetch?: boolean;
    include_additionals?: boolean;
  } = {}
) => {
  const forceRefetch = options.forceRefetch ?? false;
  const enabled = options.enabled;
  const include_additionals = options.include_additionals;

  const usePartnerContactsStore = (): EntitySliceState<PartnerContactData> => {
    return useBoundStore((state) => state.partners_contacts);
  };

  return useEntities<PartnerContactData>(
    ids,
    "partners_contacts",
    usePartnerContactsStore,
    (endpoint, params, apiOptions) =>
      useAPI(
        endpoint,
        {
          ...params,
          queryParams: {
            ...(options.computed_fields && {
              computed_fields: options.computed_fields
            }),
            ...(include_additionals && {
              include_additionals: "true"
            })
          }
        },
        { ...apiOptions, enabled }
      ),
    undefined,
    options.computed_fields,
    forceRefetch
  );
};

export const {
  useFilteredEntitiesInfinite,
  useResetFilteredEntitiesInfinite: useResetPartnerContactsInfinite
} = createUseFilteredEntitiesInfinite<PartnerContactData>(
  "partners_contacts",
  MAX_PARTNER_CONTACTS_PER_PAGE
);

export const usePartnerContactsInfinite = (
  filters?: Filters,
  search?: SearchQuery,
  groupedBy?: keyof PartnerContactData,
  pageSize = MAX_PARTNER_CONTACTS_PER_PAGE,
  sortField?: string,
  sortAsc?: boolean,
  fetchAll?: boolean,
  enabled = true,
  computed_fields: ComputedFields = ComputedFields.ALL
) => {
  return useFilteredEntitiesInfinite(
    () => ({
      addEntity: useBoundStore((state) => state.partners_contacts.addEntity)
    }),
    {
      filters,
      search,
      groupedBy,
      pageSize,
      sortField,
      sortAsc,
      fetchAll,
      enabled,
      computed_fields
    }
  );
};

export const useCreatePartnerContactMutation = () => {
  const addPartnerContact = useBoundStore(
    (state) => state.partners_contacts.addEntity
  );
  return useAPIMutation<PartnerContactData, APIObject>(
    "createPartnerContact",
    {
      method: "POST"
    },
    (response, data) => {
      addPartnerContact({
        ...data,
        id: response.id
      } as PartnerContactData);
    }
  );
};

export const useUpdatePartnerContactMutation = () => {
  const updatePartnerContact = useBoundStore(
    (state) => state.partners_contacts.updateEntity
  );
  const queryClient = useQueryClient();
  return useAPIMutation<
    { pathParams: { partnerContactId: string }; data: PartnerContactData },
    PartnerContactData
  >(
    "partners_contacts/update",
    {
      method: "PUT"
    },
    (response, data) => {
      updatePartnerContact(
        response.id,
        data.data as Partial<PartnerContactData>
      );
      refreshEntityDetail(queryClient, "partners_contacts", response.id);
    }
  );
};

export const useDeletePartnerContactMutation = () => {
  const removePartnerContact = useBoundStore(
    (state) => state.partners_contacts.removeEntity
  );
  return useAPIMutation<{ pathParams: { partnerContactId: string } }, void>(
    "partners_contacts/id",
    {
      method: "DELETE"
    },
    (_response, data) => {
      removePartnerContact(data.pathParams.partnerContactId);
    }
  );
};

export const usePartnerContactsDistinctFilterOptions = (
  fieldName: string,
  filters?: Filters,
  searchTerm?: string
) => {
  return useDistinctFilterOptions(
    "partners_contacts",
    fieldName,
    filters,
    searchTerm
  );
};
